/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
/* eslint-disable sort-keys */

/*
 * While onboarding another app in cjm.js, please identify
 * if there are sharable configs among apps and if yes, then
 * create a baseConfig object and move common configs there
*/

import {Sandbox} from '@exc/core';

// Temporary configuration while app hub svc is written.
const sideNav = {
  menu: [
    {icon: 'home', id: 'home', name: 'Home', url: '/cjm/home'},
    {
      heading: 'Journey Management',
      items: [
        // NOTE(rrp): These are not ready yet, as of Sept 2020.
        // {icon: 'trophy', id: 'goals', name: 'Goals', url: '/cjm/goals'},
        // {icon: 'reuse', id: 'lifecycles', name: 'Lifecycles', url: '/cjm/lifecycles'},
        {icon: 'journey', id: 'journeys', name: 'Journeys', url: '/cjm/journeys'}
      ]
    },
    {
      heading: 'Content Management',
      items: [
        {icon: 'message', id: 'messages', name: 'Messages', url: '/cjm/messages'},
        {icon: 'content', id: 'content', name: 'Content', url: '/cjm/content'},
        {icon: 'asset', id: 'assets', name: 'Assets', url: '/cjm/assets'}
      ]
    },
    {
      heading: 'Data Management',
      items: [
        {icon: 'segments', id: 'segments', name: 'Segments', url: '/cjm/segments'},
        {icon: 'data', id: 'data', name: 'Data', url: '/cjm/data'}
      ]
    },
    {
      heading: 'Administration',
      items: [
        {icon: 'gears', id: 'admin', name: 'Admin', url: '/cjm/admin'}
      ]
    }
  ]
};

const baseConfig = {
  access: ['acp', 'bso'],
  accessFlag: 'cjm-enabled',
  accessType: 'AND',
  releaseType: 'alpha'
};

export const cjmHome = {
  ...baseConfig,
  appId: 'cjm-home',
  distributionList: ['Grp-TrojanRabbit'],
  path: '/cjm/home',
  redirectFrom: ['/cjm'],
  refreshOnImsChange: true,
  spaAppId: 'exc-cjm-home',
  sandbox: {
    history: Sandbox.HISTORY_TYPES.HISTORY,
    sources: {
      dev: 'https://localhost.corp.adobe.com:12399'
    }
  },
  sideNav,
  palmSandbox: {
    enabled: true
  }
};

export const cjmAdmin = {
  ...baseConfig,
  appId: 'cjm-admin',
  distributionList: ['GRP-VOYAGER'],
  path: '/cjm/admin',
  sandbox: {
    history: Sandbox.HISTORY_TYPES.HISTORY,
    ims: {
      client_id: 'voyager',
      scope: 'AdobeID,openid,additional_info.projectedProductContext,read_organizations'
    },
    pathPrefix: '/admin',
    sources: {
      dev: 'https://localhost.corp.adobe.com/admin',
      qa: 'https://journeys-dev.adobe.com/admin',
      stage: 'https://journeys-stage.adobe.com/admin',
      prod: 'https://journeys.adobe.com/admin'
    }
  },
  sideNav,
  palmSandbox: {
    enabled: true
  }
};

export const cjmPlatform = {
  ...baseConfig,
  appId: 'cjm-platform',
  distributionList: [`Grp-platform-ui@adobe.com`],
  path: '/cjm/data',
  serviceCode: 'acp',
  sandbox: {
    history: Sandbox.HISTORY_TYPES.HISTORY,
    sources: {
      dev: 'https://localhost.dev.cloud.adobe.io',
      qa: 'https://platform-int.adobe.com',
      stage: 'https://platform-stage.adobe.com',
      prod: 'https://platform.adobe.com'
    }
  },
  sideNav: Object.assign({}, sideNav, {closeOnLoad: true}),
  palmSandbox: {
    enabled: true
  }
};

const baseMessage = {
  ...baseConfig,
  appId: 'cjm-message',
  distributionList: ['Grp-Content-Authoring-India'],
  sandbox: {
    history: Sandbox.HISTORY_TYPES.HISTORY,
    sources: {
      dev: 'https://localhost.corp.adobe.com:3000'
    }
  },
  sideNav,
  spaAppId: 'cjm-message-ui',
  palmSandbox: {
    enabled: true
  }
};

export const cjmMessage = Object.assign({}, baseMessage, {path: '/cjm/messages'});
const cjmContentSandbox = Object.assign({}, baseMessage.sandbox, {pathPrefix: '/content'});
export const cjmContent = Object.assign({}, baseMessage, {path: '/cjm/content', sandbox: cjmContentSandbox});

export const cjmLiveReport = {
  ...baseConfig,
  appId: 'cjm-live-report',
  distributionList: ['Grp-cjm-live-reporting-ui-team'],
  path: '/cjm/livereports',
  sandbox: {
    history: Sandbox.HISTORY_TYPES.HISTORY,
    sources: {
      dev: 'https://localhost.corp.adobe.com:3000'
    },
    ims: {
      client_id: 'cjm-live-report',
      scope: 'AdobeID, openid, read_organizations, ab.manage, additional_info, additional_info.projectedProductContext, additional_info.roles, read_pc.acp, read_pc, read_pc.dma_tartan'
    }
  },
  sideNav,
  spaAppId: 'cjm-live-reporting-ui',
  palmSandbox: {
    enabled: true
  }
};

// The solution config for the Journeys Orchestration app
export const cjmJourneys = {
  ...baseConfig,
  appId: 'cjm-journeys',
  distributionList: ['GRP-VOYAGER'],
  path: '/cjm/journeys',
  sandbox: {
    history: Sandbox.HISTORY_TYPES.HISTORY,
    ims: {
      client_id: 'voyager',
      scope: 'AdobeID,openid,additional_info.projectedProductContext,read_organizations'
    },
    sources: {
      dev: 'https://localhost.corp.adobe.com/',
      qa: 'https://journeys-dev.adobe.com/',
      stage: 'https://journeys-stage.adobe.com/',
      prod: 'https://journeys.adobe.com/'
    }
  },
  sideNav,
  palmSandbox: {
    enabled: true
  }
};

export const cjmSegments = {
  ...baseConfig,
  appId: 'cjm-segments',
  distributionList: [`Grp-platform-ui@adobe.com`],
  path: '/cjm/segments',
  sandbox: {
    history: Sandbox.HISTORY_TYPES.HISTORY,
    pathPrefix: '/segment',
    sources: {
      dev: 'https://localhost.corp.adobe.com/segments?shell_hideAppNav=true',
      qa: 'https://platform-int.adobe.com/segment?shell_hideAppNav=true',
      stage: 'https://platform-stage.adobe.com/segment?shell_hideAppNav=true',
      prod: 'https://platform.adobe.com/segment?shell_hideAppNav=true'
    }
  },
  sideNav,
  palmSandbox: {
    enabled: true
  }
};

export const cjmOffers = {
  ...baseConfig,
  appId: 'cjm-offers',
  distributionList: [`Grp-platform-ui@adobe.com`],
  path: '/cjm/offers',
  sandbox: {
    history: Sandbox.HISTORY_TYPES.HISTORY,
    pathPrefix: '/offers',
    sources: {
      dev: 'https://localhost.corp.adobe.com/offers?shell_hideAppNav=true',
      qa: 'https://platform-int.adobe.com/offers?shell_hideAppNav=true',
      stage: 'https://platform-stage.adobe.com/offers?shell_hideAppNav=true',
      prod: 'https://platform.adobe.com/offers?shell_hideAppNav=true'
    }
  },
  sideNav,
  palmSandbox: {
    enabled: true
  }
};

export const cjmAssets = {
  ...baseConfig,
  appId: 'cjm-assets',
  distributionList: ['ORG-VUROSEV-ALL@adobe.com'],
  path: '/cjm/assets',
  sandbox: {
    hideInitialSpinner: true, // DDAM shouldnt have to use this...
    history: Sandbox.HISTORY_TYPES.HASH,
    sources: {
      dev: 'https://localhost.corp.adobe.com:8443/',
      qa: 'https://experience-qa.adobe.com/solutions/ddam',
      stage: 'https://experience-stage.adobe.com/solutions/ddam',
      prod: 'https://experience.adobe.com/solutions/ddam'
    }
  },
  sideNav
};

export const cjmEmailDesigner = {
  ...baseConfig,
  appId: 'cjm-email-designer',
  distributionList: ['ORG-FINA-ALL@adobe.com'],
  path: '/cjm/content/email-designer',
  redirectFrom: ['/cjm/email-designer'],
  spaAppId: 'pixel-acrites-ui',
  sandbox: {
    hideInitialSpinner: true,
    history: Sandbox.HISTORY_TYPES.HASH,
    sources: {
      dev: 'https://localhost.rd.campaign.adobe.com:9000/'
    }
  },
  sideNav: Object.assign({}, sideNav, {closeOnLoad: true}),
  palmSandbox: {
    enabled: true
  }
};
